(() => {
  const theme = (window.theme = window.theme || {});
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  theme.prefersReducedMotion = () => reducedMotion.matches;

  if (window.Shopify && window.Shopify.designMode) {
    document.documentElement.classList.add('shopify-design-mode');
    document.body && document.body.classList.add('shopify-design-mode');
  }

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, summary, [tabindex]:not([tabindex="-1"]), [contenteditable]';

  theme.debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  theme.getFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0
    );

  let trapHandler = null;
  let trapContainer = null;
  theme.trapFocus = (container, elementToFocus) => {
    theme.releaseFocus();
    trapContainer = container;
    trapHandler = (event) => {
      if (event.key !== 'Tab') return;
      const els = theme.getFocusable(container);
      if (!els.length) {
        event.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!container.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapHandler);
    const target = elementToFocus || theme.getFocusable(container)[0] || container;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  };

  theme.releaseFocus = (elementToFocus, container) => {
    // Un composant ne libère que son propre piège (la recherche ne casse pas celui du tiroir panier)
    if (container && trapContainer && container !== trapContainer) return;
    if (trapHandler) document.removeEventListener('keydown', trapHandler);
    trapHandler = null;
    trapContainer = null;
    if (elementToFocus && document.contains(elementToFocus)) elementToFocus.focus({ preventScroll: true });
  };

  let scrollLocks = 0;
  theme.lockScroll = () => {
    scrollLocks++;
    document.body.classList.add('is-scroll-locked');
  };
  theme.unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.body.classList.remove('is-scroll-locked');
  };

  theme.announce = (message) => {
    const region = document.getElementById('A11yLiveRegion');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => (region.textContent = message), 50);
  };

  theme.formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? withSep + decimal + dec : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount':
          return fmt(value, 2);
        case 'amount_no_decimals':
          return fmt(value, 0);
        case 'amount_with_comma_separator':
          return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return fmt(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return fmt(value, 0, ' ', ',');
        default:
          return fmt(value, 2);
      }
    });
  };

  theme.fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(options.headers || {}) }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      const error = new Error(data.description || data.message || theme.strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  theme.fetchSection = async (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString());
    if (!response.ok) throw new Error(`Section ${sectionId} failed`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  };

  theme.replaceFrom = (doc, selector, root = document) => {
    const source = doc.querySelector(selector);
    const target = root.querySelector(selector);
    if (source && target) target.replaceWith(source);
    return source;
  };

  const listeners = {};
  theme.subscribe = (event, cb) => {
    (listeners[event] = listeners[event] || []).push(cb);
    return () => (listeners[event] = listeners[event].filter((f) => f !== cb));
  };
  theme.publish = (event, data) => (listeners[event] || []).forEach((cb) => cb(data));

  theme.track = (name, data = {}) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (error) {
      /* le suivi ne doit jamais bloquer le parcours d'achat */
    }
  };

  theme.productContext = (el) => {
    const root = el && el.closest ? el.closest('[data-product-root]') : null;
    const source = root && root.querySelector('script[data-product-analytics]');
    if (!source) return {};
    try {
      const product = JSON.parse(source.textContent);
      const input = root.querySelector('[data-variant-input]');
      const variantId = Number(input && input.value) || product.variant_id;
      const variant = product.variants.find((v) => v.id === variantId) || {};
      const qty = root.querySelector('[data-main-quantity]');
      return {
        product_id: product.product_id,
        product_title: product.title,
        product_type: product.type,
        variant_id: variantId,
        variant_title: variant.title,
        sku: variant.sku,
        price: (variant.price || 0) / 100,
        currency: product.currency,
        quantity: Number(qty && qty.value) || 1
      };
    } catch (error) {
      return {};
    }
  };

  theme.cartSections = () =>
    Array.from(document.querySelectorAll('[data-cart-section]')).map((el) => el.dataset.cartSection);

  theme.cart = {
    async get() {
      return theme.fetchJSON(`${theme.routes.cart}.js`);
    },
    async add(items, extra = {}) {
      const sections = theme.cartSections();
      const body = { items, ...extra };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      const data = await theme.fetchJSON(`${theme.routes.cartAdd}.js`, { method: 'POST', body: JSON.stringify(body) });
      theme.publish('cart:change', { source: 'add', data });
      return data;
    },
    async change(lineOrKey, quantity) {
      const sections = theme.cartSections();
      // Une clé (texte) reste valable même si les lignes sont renumérotées entre deux requêtes
      const ref = typeof lineOrKey === 'string' ? { id: lineOrKey } : { line: lineOrKey };
      const data = await theme.fetchJSON(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({ ...ref, quantity, sections, sections_url: window.location.pathname })
      });
      theme.publish('cart:change', { source: 'change', data });
      return data;
    },
    async update(payload) {
      const sections = theme.cartSections();
      const data = await theme.fetchJSON(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        body: JSON.stringify({ ...payload, sections, sections_url: window.location.pathname })
      });
      theme.publish('cart:change', { source: 'update', data });
      return data;
    },
    async refresh() {
      const sections = theme.cartSections();
      if (!sections.length) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.join(','));
      const response = await fetch(url.toString());
      const data = await response.json();
      theme.publish('cart:change', { source: 'refresh', data: { sections: data } });
    }
  };

  theme.renderCartSections = (sectionsData) => {
    if (!sectionsData) return;
    Object.entries(sectionsData).forEach(([id, html]) => {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
        const source = doc.querySelector(`[data-cart-section="${id}"]`);
        if (!source) return;
        const selectors = (target.dataset.cartReplace || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (selectors.length) {
          selectors.forEach((selector) => {
            const from = source.querySelector(selector);
            const to = target.querySelector(selector);
            if (from && to) to.replaceWith(from);
          });
        } else {
          target.innerHTML = source.innerHTML;
        }
      });
    });
  };

  theme.subscribe('cart:change', ({ data }) => theme.renderCartSections(data && data.sections));

  // iOS Safari only applies :active (press feedback) when a touch listener exists
  document.addEventListener('touchstart', () => {}, { passive: true });

  class DrawerComponent extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');
      this.setupSwipe();
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      });
      if (this.id) {
        this.onDocumentClick = (event) => {
          const opener = event.target.closest(`[aria-controls="${this.id}"]`);
          if (!opener || opener.tagName === 'SUMMARY') return;
          if (opener.dataset.drawerToggle !== undefined && this.isOpen) return this.close();
          if (this.contains(opener)) return;
          event.preventDefault();
          this.open(opener);
        };
        document.addEventListener('click', this.onDocumentClick);
      }
    }

    disconnectedCallback() {
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      if (this.isOpen) {
        theme.unlockScroll();
        theme.releaseFocus(null, this.panel || this);
      }
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // Swipe toward the edge to close: the panel follows the finger 1:1 and the
    // release velocity is projected to decide between closing and snapping back
    setupSwipe() {
      const panel = this.panel;
      if (!panel || theme.prefersReducedMotion()) return;
      let gesture = null;

      const scrollsSideways = (el) => {
        for (; el && el !== panel; el = el.parentElement) {
          const overflow = getComputedStyle(el).overflowX;
          if ((overflow === 'auto' || overflow === 'scroll') && el.scrollWidth > el.clientWidth + 1) return true;
        }
        return false;
      };

      panel.addEventListener(
        'touchstart',
        (event) => {
          if (!this.isOpen || event.touches.length !== 1 || scrollsSideways(event.target)) return;
          const touch = event.touches[0];
          // Panel on the left closes to the left (-1), otherwise to the right (1)
          const side = panel.getBoundingClientRect().left < 1 ? -1 : 1;
          gesture = { x: touch.clientX, y: touch.clientY, side, tracking: false, offset: 0, history: [] };
        },
        { passive: true }
      );

      panel.addEventListener(
        'touchmove',
        (event) => {
          if (!gesture) return;
          const touch = event.touches[0];
          const dx = touch.clientX - gesture.x;
          const dy = touch.clientY - gesture.y;
          if (!gesture.tracking) {
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
            // Only a sideways move toward the edge is a swipe; anything else stays a scroll
            if (Math.abs(dx) <= Math.abs(dy) || dx * gesture.side <= 0) {
              gesture = null;
              return;
            }
            gesture.tracking = true;
            gesture.x = touch.clientX; // start from here: no jump past the 10px threshold
            panel.style.transition = 'none';
          }
          gesture.offset = Math.max(0, (touch.clientX - gesture.x) * gesture.side);
          panel.style.transform = `translateX(${gesture.offset * gesture.side}px)`;
          gesture.history.push({ x: touch.clientX, t: event.timeStamp });
          if (gesture.history.length > 5) gesture.history.shift();
        },
        { passive: true }
      );

      const release = () => {
        if (!gesture || !gesture.tracking) {
          gesture = null;
          return;
        }
        const { history, side, offset } = gesture;
        gesture = null;
        const first = history[0];
        const last = history[history.length - 1];
        const velocity = first && last && last.t > first.t ? (((last.x - first.x) / (last.t - first.t)) * 1000 * side) : 0;
        // Where the panel would come to rest if thrown (scroll-like deceleration)
        const projected = offset + ((velocity / 1000) * 0.998) / (1 - 0.998);
        const shouldClose = projected > panel.offsetWidth / 2;
        panel.style.transition = '';
        panel.style.transform = '';
        if (shouldClose) {
          panel.classList.add('is-flung');
          panel.addEventListener('transitionend', () => panel.classList.remove('is-flung'), { once: true });
          setTimeout(() => panel.classList.remove('is-flung'), 400);
          this.close();
        }
      };
      panel.addEventListener('touchend', release);
      panel.addEventListener('touchcancel', release);
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      this.panel && this.panel.removeAttribute('inert');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      theme.lockScroll();
      theme.trapFocus(this.panel || this, this.querySelector('[data-autofocus]'));
      if (this.id === 'CartDrawer') theme.track('vtx_cart_drawer_open', {});
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      theme.unlockScroll();
      theme.releaseFocus(this.trigger, this.panel || this);
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }
  }
  if (!customElements.get('drawer-component')) customElements.define('drawer-component', DrawerComponent);

  class ModalOpener extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button, a');
      if (!button) return;
      button.addEventListener('click', (event) => {
        const dialog = document.getElementById(this.dataset.modal);
        if (!dialog) return;
        event.preventDefault();
        theme.openDialog(dialog, button);
      });
    }
  }
  if (!customElements.get('modal-opener')) customElements.define('modal-opener', ModalOpener);

  theme.openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || document.activeElement;
    if (!dialog._bound) {
      dialog._bound = true;
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-close]')) dialog.close();
      });
      dialog.addEventListener('close', () => {
        theme.unlockScroll();
        if (dialog._trigger && document.contains(dialog._trigger)) dialog._trigger.focus({ preventScroll: true });
        dialog.dispatchEvent(new CustomEvent('dialog:closed', { bubbles: true }));
      });
    }
    theme.lockScroll();
    dialog.showModal();
  };

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-slider]');
      if (!this.slider) return;
      this.prev = this.querySelectorAll('[data-slider-prev]');
      this.next = this.querySelectorAll('[data-slider-next]');
      this.bar = this.querySelector('.slider-progress__bar');
      this.prev.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.next.forEach((b) => b.addEventListener('click', () => this.go(1)));
      this.onScroll = () => requestAnimationFrame(() => this.update());
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.slider);
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver && this.resizeObserver.disconnect();
    }

    get slides() {
      return Array.from(this.slider.children).filter((el) => el.offsetParent !== null);
    }

    go(direction) {
      const first = this.slides[0];
      if (!first) return;
      const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
      const step = first.getBoundingClientRect().width + gap;
      const visible = Math.max(1, Math.floor((this.slider.clientWidth + gap) / step));
      this.slider.scrollBy({ left: direction * step * visible, behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      this.slider.scrollTo({ left: slide.offsetLeft - this.slider.offsetLeft, behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.slider;
      const max = scrollWidth - clientWidth;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft >= max - 2;
      this.prev.forEach((b) => (b.disabled = atStart));
      this.next.forEach((b) => (b.disabled = atEnd));
      const hidden = max <= 2;
      this.toggleAttribute('data-static', hidden);
      if (this.bar) {
        const size = Math.min(100, (clientWidth / scrollWidth) * 100);
        const ratio = max > 0 ? scrollLeft / max : 0;
        this.bar.style.setProperty('--progress-size', `${size}%`);
        this.bar.style.setProperty('--progress-offset', `${ratio * (100 / size - 1) * 100}%`);
      }
      this.dispatchEvent(new CustomEvent('slider:update', { detail: { scrollLeft, max } }));
    }
  }
  if (!customElements.get('slider-component')) customElements.define('slider-component', SliderComponent);

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button[name]').forEach((button) =>
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const previous = this.input.value;
          button.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
          if (previous !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateButtons();
        })
      );
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }

    updateButtons() {
      const value = parseInt(this.input.value, 10) || 0;
      const min = parseInt(this.input.min, 10) || 0;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = max !== null && value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  theme.openCart = (trigger) => {
    const drawer = document.getElementById('CartDrawer');
    if (drawer && theme.cartType === 'drawer') drawer.open(trigger);
    else window.location.href = theme.routes.cart;
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.errorWrapper = this.querySelector('[data-form-error]');
      // event.submitter n'existe pas sur Safari iOS < 15.4 : on retient le dernier bouton utilisé,
      // y compris ceux reliés par l'attribut form="" (barre d'ajout fixe)
      this.onSubmitterClick = (event) => {
        const button = event.target.closest('[type="submit"]');
        if (!button) return;
        if (this.form.contains(button) || (this.form.id && button.getAttribute('form') === this.form.id)) this.lastSubmitter = button;
      };
      document.addEventListener('click', this.onSubmitterClick, true);
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    disconnectedCallback() {
      if (this.onSubmitterClick) document.removeEventListener('click', this.onSubmitterClick, true);
    }

    async onSubmit(event) {
      const submitter = event.submitter || this.lastSubmitter || this.submitButton;
      this.lastSubmitter = null;
      // Taille pas encore choisie : on la demande au lieu de ne rien faire
      if (theme.showPendingOption(this.closest('[data-product-root]') || this)) {
        event.preventDefault();
        return;
      }
      const idInput = this.form.querySelector('[name="id"]');
      // Variante épuisée ou combinaison inexistante : rien n'est envoyé, quel que soit le mode du panier
      if (!submitter || submitter.getAttribute('aria-disabled') === 'true' || (idInput && !idInput.value)) {
        event.preventDefault();
        return;
      }
      const isBuyNow = submitter.hasAttribute('data-buy-now');
      const context = theme.productContext(this);
      if (isBuyNow) theme.track('vtx_buy_now_click', context);
      if (submitter.closest('sticky-atc')) theme.track('vtx_sticky_atc_click', context);

      const bundles = Array.from(document.querySelectorAll(`[data-bundle-for="${this.form.id}"]:checked`));
      const hasDrawer = theme.cartType === 'drawer' && document.getElementById('CartDrawer');
      // Sans JS, le formulaire fonctionne nativement (« Commander maintenant » porte return_to).
      // Avec JS, on passe par l'API panier pour inclure les compléments cochés et afficher les erreurs de stock.
      if (!isBuyNow && !bundles.length && !hasDrawer) return;
      event.preventDefault();

      this.setError();
      submitter.classList.add('is-loading');
      submitter.setAttribute('aria-disabled', 'true');
      const formData = new FormData(this.form);
      const items = [];
      const main = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1) };
      const properties = {};
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value) properties[match[1]] = value;
      }
      if (Object.keys(properties).length) main.properties = properties;
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) main.selling_plan = Number(sellingPlan);
      items.push(main);
      bundles.forEach((input) => {
        const variantId = Number(input.value);
        if (variantId) items.push({ id: variantId, quantity: 1 });
      });
      try {
        await theme.cart.add(items);
        this.dispatchEvent(new CustomEvent('product:added', { bubbles: true }));
        if (isBuyNow) {
          window.location.href = submitter.value || '/checkout';
          return;
        }
        if (!hasDrawer) {
          window.location.href = theme.routes.cart;
          return;
        }
        const modal = this.closest('dialog');
        if (modal && modal.open) modal.close();
        theme.openCart(submitter);
      } catch (error) {
        this.setError(error.message);
      }
      submitter.classList.remove('is-loading');
      if (!submitter.hasAttribute('data-unavailable')) submitter.removeAttribute('aria-disabled');
    }

    setError(message) {
      if (!this.errorWrapper) return;
      this.errorWrapper.hidden = !message;
      const text = this.errorWrapper.querySelector('[data-form-error-text]') || this.errorWrapper;
      text.textContent = message || '';
      if (message) theme.announce(message);
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  // ---------- Paiement à la livraison (EasySell) ----------
  // EasySell est réglé sur « panier uniquement », réglage commun à tous les thèmes.
  // Sur ce thème seulement, on l'autorise aussi sur la fiche produit avant que son script démarre
  // (theme.js est chargé avant lui). Si EasySell change, openTysloForm n'existe pas et le bouton
  // reprend le parcours panier (?easysell=1) : rien ne casse.
  // Si EasySell est déjà réglé sur « fiche + panier », ses propres boutons s'ajoutent à ceux du thème :
  // la classe es-product-form les masque dans tous les cas (sinon un 2e bouton « Acheter maintenant »).
  if (theme.codProductForm && window.ES_CURRENT_PAGE === 'product') {
    const esConfig = window.EASYSELL_CONFIG;
    if (esConfig && esConfig.settings && esConfig.settings.placement === 'cart') {
      esConfig.settings.placement = 'all';
      esConfig.settings.sticky = false; // la barre fixe du thème suffit
    }
    document.documentElement.classList.add('es-product-form');
  }

  // Option obligatoire (taille) laissée vide à l'arrivée sur la fiche : voir data-required-option dans product-variant-picker.liquid
  theme.pendingOption = (root) => {
    const group = root && root.querySelector('[data-required-option]');
    return group && !group.querySelector('input:checked') ? group : null;
  };

  theme.showPendingOption = (root) => {
    const group = theme.pendingOption(root);
    if (!group) return false;
    const hint = group.querySelector('[data-option-hint]');
    if (hint) hint.hidden = false;
    group.classList.remove('is-required');
    void group.offsetWidth; // relance l'animation à chaque clic
    group.classList.add('is-required');
    group.scrollIntoView({ block: 'center', behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
    const first = group.querySelector('input:not(.is-unavailable)') || group.querySelector('input');
    if (first) first.focus({ preventScroll: true });
    if (hint) theme.announce(hint.textContent);
    return true;
  };

  // Bouton « Commander » de la fiche et de la barre fixe.
  // Si EasySell autorise le formulaire sur la fiche produit, il s'ouvre sur place.
  // Sinon (EasySell réglé sur « panier »), l'article est ajouté et la page panier ouvre le formulaire d'elle-même (?easysell=1).
  theme.codOrder = async (button) => {
    const root = button.closest('[data-product-root]') || document;
    const form = button.form || root.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || button.classList.contains('is-loading')) return;
    const context = { ...theme.productContext(form), source: button.dataset.codOrder };
    if (theme.showPendingOption(root)) {
      theme.track('vtx_cod_size_missing', context);
      return;
    }
    if (button.getAttribute('aria-disabled') === 'true') return;
    const idInput = form.querySelector('[name="id"]');
    const variantId = Number(idInput && idInput.value);
    if (!variantId) return;
    theme.track('vtx_cod_click', context);

    if (typeof window.openTysloForm === 'function') {
      window.ES_CLOSEST_FORM = form;
      window.openTysloForm();
      return;
    }

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    try {
      const quantity = Number(new FormData(form).get('quantity') || 1);
      const cart = await theme.cart.get();
      // Un double clic ou un retour arrière ne doit pas commander deux fois la même pièce
      if (!cart.items.some((item) => item.variant_id === variantId)) await theme.cart.add([{ id: variantId, quantity }]);
      window.location.href = `${theme.routes.cart}?easysell=1`;
    } catch (error) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      const productForm = form.closest('product-form');
      if (productForm && productForm.setError) productForm.setError(error.message || theme.strings.cartError);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('[data-cod-order]');
    if (!button) return;
    event.preventDefault();
    theme.codOrder(button);
  });

  // Libellés du bouton « Commander » selon la variante choisie
  document.addEventListener('variant:change', (event) => {
    const root = event.target.closest('[data-product-root]') || document;
    const variant = event.detail && event.detail.variant;
    const pending = theme.pendingOption(root);
    root.querySelectorAll('[data-required-option]').forEach((group) => {
      if (group.querySelector('input:checked')) {
        group.classList.remove('is-required');
        const hint = group.querySelector('[data-option-hint]');
        if (hint) hint.hidden = true;
      }
    });
    const pendingText = pending && pending.querySelector('[data-option-hint]') ? pending.querySelector('[data-option-hint]').textContent : '';
    root.querySelectorAll('[data-cod-order]').forEach((button) => {
      const label = button.querySelector('[data-cod-label]');
      if (!label) return;
      if (pending) label.textContent = button.dataset.codOrder === 'main' && pendingText ? pendingText : button.dataset.codReady;
      else if (!variant) label.textContent = theme.strings.unavailable;
      else label.textContent = variant.available ? button.dataset.codReady : theme.strings.soldOut;
    });
  });

  // Mesure : ouverture du formulaire EasySell et commande validée (Paramètres > Événements clients)
  const chainHook = (name, callback) => {
    const previous = window[name];
    window[name] = function (...args) {
      try {
        callback(...args);
      } catch (error) {
        /* le suivi ne doit jamais bloquer la commande */
      }
      if (typeof previous === 'function') return previous.apply(this, args);
    };
  };
  chainHook('es_hook_open_popup', () => theme.track('vtx_cod_form_open', { page: window.location.pathname }));
  chainHook('es_hook_purchase_success', () => theme.track('vtx_cod_order_success', { page: window.location.pathname }));

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl === 'true';
      const json = this.querySelector('script[type="application/json"]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', (event) => this.onChange(event));
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-position]')).map((group) => {
        const checked = group.querySelector('input:checked');
        const select = group.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }

    onChange(event) {
      const group = event.target.closest('[data-option-position]');
      if (group) {
        const label = group.querySelector('[data-selected-value]');
        if (label) label.textContent = event.target.value;
      }
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, sectionId: this.sectionId } }));
      this.renderVariant(variant);
    }

    async renderVariant(variant) {
      const requestId = (this.requestId = (this.requestId || 0) + 1);
      const root = this.closest('[data-product-root]') || document;
      const formInputs = root.querySelectorAll(`[data-variant-input="${this.sectionId}"]`);
      formInputs.forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      const buttons = root.querySelectorAll(`[data-add-button="${this.sectionId}"]`);
      // Taille pas encore choisie : ce n'est pas une variante indisponible, les boutons restent actifs pour la demander
      if (!variant && theme.pendingOption(root)) {
        buttons.forEach((b) => {
          b.removeAttribute('aria-disabled');
          b.removeAttribute('data-unavailable');
        });
        return;
      }
      if (!variant) {
        buttons.forEach((b) => {
          b.setAttribute('aria-disabled', 'true');
          b.setAttribute('data-unavailable', '');
          const label = b.querySelector('[data-add-label]');
          if (label) label.textContent = theme.strings.unavailable;
        });
        theme.announce(theme.strings.unavailable);
        return;
      }
      if (this.updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      if (variant.featured_media) {
        document.dispatchEvent(new CustomEvent('media:select', { detail: { mediaId: variant.featured_media, sectionId: this.sectionId } }));
      }
      try {
        const url = `${this.productUrl}${this.productUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
        const doc = await theme.fetchSection(url, this.dataset.renderSection || this.sectionId);
        if (requestId !== this.requestId) return;
        root.querySelectorAll('[data-variant-dynamic]').forEach((target) => {
          const key = target.dataset.variantDynamic;
          const source = doc.querySelector(`[data-variant-dynamic="${key}"]`);
          if (source) target.innerHTML = source.innerHTML;
        });
        root.querySelectorAll('[data-option-position]').forEach((group) => {
          const source = doc.querySelector(`[data-option-position="${group.dataset.optionPosition}"]`);
          if (!source) return;
          source.querySelectorAll('input').forEach((input) => {
            const target = group.querySelector(`input[value="${CSS.escape(input.value)}"]`);
            if (target) target.classList.toggle('is-unavailable', input.classList.contains('is-unavailable'));
          });
        });
      } catch (error) {
        console.error(error);
      }
      if (requestId !== this.requestId) return;
      buttons.forEach((b) => {
        const label = b.querySelector('[data-add-label]');
        if (variant.available) {
          b.removeAttribute('aria-disabled');
          b.removeAttribute('data-unavailable');
          if (label) label.textContent = theme.strings.addToCart;
        } else {
          b.setAttribute('aria-disabled', 'true');
          b.setAttribute('data-unavailable', '');
          if (label) label.textContent = theme.strings.soldOut;
        }
      });
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  class CardSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.card');
      this.addEventListener('click', (event) => {
        const swatch = event.target.closest('.swatch');
        if (!swatch) return;
        event.preventDefault();
        this.select(swatch);
      });
    }

    select(swatch) {
      this.querySelectorAll('.swatch').forEach((s) => s.setAttribute('aria-pressed', String(s === swatch)));
      const img = this.card.querySelector('.card__media img');
      if (img && swatch.dataset.src) {
        img.src = swatch.dataset.src;
        img.srcset = swatch.dataset.srcset || '';
      }
      this.card.querySelectorAll('[data-card-link]').forEach((link) => {
        if (swatch.dataset.url) link.href = swatch.dataset.url;
      });
      const label = this.card.querySelector('[data-card-variant]');
      if (label) label.textContent = swatch.dataset.label || '';
    }
  }
  if (!customElements.get('card-swatches')) customElements.define('card-swatches', CardSwatches);

  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-content]');
      this.removeAttribute('hidden');
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-quick-add]');
        if (!opener) return;
        event.preventDefault();
        this.show(opener);
      });
      this.dialog.addEventListener('close', () => (this.content.innerHTML = ''));
    }

    async show(opener) {
      opener.classList.add('is-loading');
      try {
        const doc = await theme.fetchSection(opener.dataset.quickAdd, 'quick-add');
        const product = doc.querySelector('[data-quick-add-content]');
        if (!product) throw new Error('No quick add content');
        this.content.innerHTML = '';
        this.content.appendChild(product);
        product.querySelectorAll('script').forEach((old) => {
          const script = document.createElement('script');
          Array.from(old.attributes).forEach((a) => script.setAttribute(a.name, a.value));
          script.textContent = old.textContent;
          old.replaceWith(script);
        });
        theme.openDialog(this.dialog, opener);
      } catch (error) {
        window.location.href = opener.dataset.quickAdd;
      } finally {
        opener.classList.remove('is-loading');
      }
    }
  }
  if (!customElements.get('quick-add-modal')) customElements.define('quick-add-modal', QuickAddModal);

  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.removeAttribute('hidden');
      this.style.display = 'none';
      this.querySelector('button').addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
        const main = document.getElementById('MainContent');
        main && main.focus({ preventScroll: true });
      });
      // On n'écrit dans le style que si l'état change (pas de travail inutile à chaque défilement)
      let shown = null;
      let ticking = false;
      const update = () => {
        ticking = false;
        const show = window.scrollY > window.innerHeight;
        if (show === shown) return;
        shown = show;
        this.style.display = show ? '' : 'none';
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      update();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  if (!customElements.get('back-to-top')) customElements.define('back-to-top', BackToTop);

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-autosubmit]')) this.querySelector('form').submit();
      });
      this.addEventListener('click', (event) => {
        const option = event.target.closest('[data-value]');
        if (!option) return;
        event.preventDefault();
        const form = option.closest('form');
        form.querySelector(`input[name="${option.dataset.name}"]`).value = option.dataset.value;
        form.submit();
      });
    }
  }
  if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

  class DetailsDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.details.open = false;
          this.summary.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.details.open = false;
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.details.open = false;
      });
      this.details.addEventListener('toggle', () => this.summary.setAttribute('aria-expanded', String(this.details.open)));

      // Header dropdowns open on hover with a mouse (click and keyboard still work)
      if (this.hasAttribute('data-hover') && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        let timer;
        this.addEventListener('mouseenter', () => {
          clearTimeout(timer);
          document.querySelectorAll('details-disclosure[data-hover] details[open]').forEach((other) => other !== this.details && (other.open = false));
          this.details.open = true;
        });
        this.addEventListener('mouseleave', () => {
          timer = setTimeout(() => (this.details.open = false), 150);
        });
      }
    }
  }
  if (!customElements.get('details-disclosure')) customElements.define('details-disclosure', DetailsDisclosure);

  theme.initReveal = (root = document) => {
    const els = root.querySelectorAll('.reveal:not(.is-revealed)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || theme.prefersReducedMotion() || !document.body.classList.contains('has-reveal')) {
      els.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    els.forEach((el) => observer.observe(el));
  };

  theme.whenVisible = (element, callback, options = { rootMargin: '200px' }) => {
    if (!('IntersectionObserver' in window)) return callback(true);
    const observer = new IntersectionObserver((entries) => entries.forEach((e) => callback(e.isIntersecting)), options);
    observer.observe(element);
    return observer;
  };

  const ready = () => {
    theme.initReveal();
    if (theme.cartType === 'drawer') {
      document.addEventListener('click', (event) => {
        const link = event.target.closest('[data-cart-toggle]');
        if (!link || !document.getElementById('CartDrawer')) return;
        event.preventDefault();
        theme.openCart(link);
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  document.addEventListener('shopify:section:load', (event) => theme.initReveal(event.target));

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('.is-loading').forEach((el) => {
      el.classList.remove('is-loading');
      if (!el.hasAttribute('data-unavailable')) el.removeAttribute('aria-disabled');
    });
    theme.cart.refresh().catch(() => {});
  });

  // Titres sur une seule ligne ([data-fit-text]) : si le texte dépasse, on réduit la taille, sans descendre sous 16 px
  theme.fitText = (el) => {
    el.style.fontSize = '';
    let size = parseFloat(getComputedStyle(el).fontSize);
    while (el.scrollWidth > el.clientWidth + 1 && size > 16) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
  };
  const fitAll = () => document.querySelectorAll('[data-fit-text]').forEach(theme.fitText);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fitAll);
  else fitAll();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
  let fitFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(fitFrame);
    fitFrame = requestAnimationFrame(fitAll);
  });
  document.addEventListener('shopify:section:load', fitAll);

  // Événements personnalisés pour les pixels (Paramètres > Événements clients)
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('[data-whatsapp]')) {
        theme.track('vtx_whatsapp_click', { page: window.location.pathname, ...theme.productContext(document.querySelector('[data-product-root]')) });
      }
      const guide = target.closest('modal-opener[data-modal^="SizeGuide"]');
      if (guide) theme.track('vtx_size_guide_open', theme.productContext(guide));
      const codCart = target.closest('[data-cod-cart]');
      if (codCart) {
        theme.track('vtx_cod_click', { source: 'drawer' });
        // Sur une fiche produit, le formulaire s'ouvre sur place avec le contenu du panier
        if (typeof window.openTysloForm === 'function') {
          event.preventDefault();
          const drawer = document.getElementById('CartDrawer');
          if (drawer && drawer.close) drawer.close();
          window.openTysloForm(true);
        }
      }
      const quickAdd = target.closest('[data-quick-add]');
      if (quickAdd) theme.track('vtx_quick_add_open', { url: quickAdd.dataset.quickAdd });
    },
    true
  );

  // Product cards side by side: each price drops to the lowest one in its row, so prices
  // line up even when a title or colour line is longer, without empty space when all are short
  let alignFrame = null;
  const alignCardPrices = () => {
    alignFrame = null;
    const rows = new Map();
    const infos = [];
    document.querySelectorAll('.card__info').forEach((info) => {
      const price = info.querySelector(':scope > .price');
      if (!price) return;
      price.style.marginTop = '';
      infos.push({ info, price });
    });
    infos.forEach(({ info, price }) => {
      if (!info.offsetParent) return;
      const key = Math.round((info.getBoundingClientRect().top + window.scrollY) / 4);
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push({ info, price });
    });
    const updates = [];
    rows.forEach((cards) => {
      if (cards.length < 2) return;
      const offsets = cards.map(({ info, price }) => price.getBoundingClientRect().top - info.getBoundingClientRect().top);
      const lowest = Math.max(...offsets);
      cards.forEach(({ price }, i) => {
        const gap = lowest - offsets[i];
        if (gap > 0.5) updates.push([price, gap]);
      });
    });
    updates.forEach(([price, gap]) => (price.style.marginTop = `${gap}px`));
  };
  const scheduleAlign = () => {
    if (!alignFrame) alignFrame = setTimeout(alignCardPrices, 50);
  };
  window.addEventListener('load', scheduleAlign);
  window.addEventListener('resize', scheduleAlign);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleAlign);
  document.addEventListener('DOMContentLoaded', scheduleAlign);
  // Cards added later (filters, "load more", recommendations)
  const touchesCards = (node) => node.nodeType === 1 && (node.matches('.card__info, .card__info *') || node.querySelector('.card__info'));
  new MutationObserver((mutations) => {
    if (mutations.some((m) => [...m.addedNodes].some(touchesCards) || (m.target.nodeType === 1 && m.target.closest('.card__info')))) scheduleAlign();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
